'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';
import { useContext, useRef, useEffect } from 'react';

import { DialogContext } from '@/components/features/dialogs/infra/context/DialogContext';
import Icon, { IconName } from '@/components/ui/Icon';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';
import { typographyVariants } from './typography';
const Dialog = ({
  open,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error(
      'DialogContext not found. Make sure your component is wrapped in a <DialogProvider>.'
    );
  }
  const { incrementOpenDialogs, decrementOpenDialogs } = context;
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      incrementOpenDialogs();
      wasOpen.current = true;
    } else if (!open && wasOpen.current) {
      decrementOpenDialogs();
      wasOpen.current = false;
    }
  }, [open, incrementOpenDialogs, decrementOpenDialogs]);

  return <DialogPrimitive.Root open={open} {...props} />;
};

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/70 backdrop-blur-xs',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideClose?: boolean;
    fullScreen?: boolean;
    fullWidth?: boolean;
  }
>(
  (
    { className, children, hideClose, fullScreen, fullWidth, ...props },
    ref
  ) => {
    const [isMobile] = useIsMobile();

    return (
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 flex max-h-[100vh] w-11/12 translate-x-[-50%] translate-y-[-50%] flex-col gap-4 overflow-x-hidden overflow-y-auto rounded-md border py-10 shadow-lg duration-200 md:max-w-md',
            fullWidth ? 'px-0' : 'px-10',
            fullScreen && isMobile
              ? 'h-full w-full rounded-none px-6 py-6'
              : '',
            className
          )}
          {...props}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
          }}
        >
          {children}
          {!hideClose && (
            <DialogPrimitive.Close
              className={cn(
                'ring-offset-background data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-hidden disabled:pointer-events-none',
                fullScreen ? 'right-4' : ''
              )}
            >
              <X className='h-6 w-6' />
              <span className='sr-only'>Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex grow-0 flex-col space-y-3 text-center sm:text-left',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'mt-auto flex flex-row items-center justify-center space-x-2',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      `${typographyVariants({ variant: 'h2' })} text-center capitalize`,
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('my-4 text-center font-normal', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogHeaderWithoutPadding = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      '-mt-6 -ml-6 w-screen shrink-0 pt-7 pb-4 pl-8 shadow md:w-full md:shadow-none',
      className
    )}
    {...props}
  >
    <DialogClose className='hover:text-black-800 bg-white-400 mb-4 self-start rounded-full p-2 shadow'>
      <Icon variant={IconName.ArrowLeft} />
    </DialogClose>
    {children}
  </div>
);

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogHeaderWithoutPadding,
};
